import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, ShieldCheck } from "lucide-react";
import { startOfDay } from "date-fns";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { MoneyText } from "@/components/ui/MoneyText";
import { Progress } from "@/components/ui/Progress";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Mascot } from "@/components/brand/Mascot";
import { ParentMascot } from "@/components/brand/ParentMascot";
import { genderFromRelation, relationLabel } from "@/lib/utils/roles";
import { parentMascotSpeech } from "@/lib/utils/parentSpeech";
import {
  getNextBlogPost,
} from "@/lib/data/blogPosts";
import { markMomentSeen, markMomentClicked } from "@/lib/db/userPrefs";
import {
  childMascotSpeech,
  type ChildMascotMoment,
} from "@/lib/utils/childSpeech";
import { GoalProgressInline } from "@/components/goals/GoalProgressInline";
import { useAuth } from "@/lib/auth/context";
import { useChores } from "@/lib/hooks/useChores";
import { useMembers } from "@/lib/hooks/useMembers";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useChoreProposals } from "@/lib/hooks/useChoreProposals";
import { useInstantSubmissions } from "@/lib/hooks/useInstantSubmissions";
import { useTodaysCompletions } from "@/lib/hooks/useTodaysCompletions";
import { useGoals } from "@/lib/hooks/useGoals";
import { useHouseholdGoals } from "@/lib/hooks/useHouseholdGoals";
import { useGoalProgress } from "@/lib/hooks/useGoalProgress";
import { useAutoMarkGoalCompletion } from "@/lib/hooks/useAutoMarkGoalCompletion";
import { isChoreActiveToday, isChoreForUser } from "@/lib/utils/chores";
import { sortGoalsByPriority } from "@/lib/utils/goals";
import { cn } from "@/lib/utils/cn";
import { GoalDialog } from "@/components/goals/GoalDialog";
import { FamilyGoalCard } from "@/components/goals/FamilyGoalCard";
import { pickFamilyGoalForDisplay } from "@/lib/utils/goalDisplay";
import { CompletionSubmitSheet } from "@/components/chores/CompletionSubmitSheet";
import { ChoreTaskCard } from "@/components/chores/ChoreTaskCard";
import { TreasureMeadowCard } from "@/components/treasure/TreasureMeadowCard";
import { TreasureModal } from "@/components/treasure/TreasureModal";
import { TreasureCollectionSheet } from "@/components/treasure/TreasureCollectionSheet";
import { HeroAvatar } from "@/components/treasure/HeroAvatar";
import { AddChildSheet } from "@/components/settings/AddChildSheet";
import { FirstWeekChecklist } from "@/components/screens/FirstWeekChecklist";
import { WeeklyFunderBreakdown } from "@/components/wallet/WeeklyFunderBreakdown";
import { useHouseholdEarnsThisWeek } from "@/lib/hooks/useHouseholdEarnsThisWeek";
import { RitualBanner } from "@/components/rituals/RitualBanner";
import { SundayPlanBanner } from "@/components/rituals/SundayPlanBanner";
import type { Chore } from "@/lib/schemas";
import {
  computeTreasureProgress,
  normalizeTreasurePrefs,
  treasureCompanionMessage,
  treasureMeadowStage,
  treasureRewardById,
} from "@/lib/utils/treasure";

// ─── ChildDashboard — Sianko v2 (Mix direction) ────────────────────────────

export function ChildDashboard() {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;

  const { chores } = useChores(hid);
  const { members } = useMembers(hid);
  const { completions: todays } = useTodaysCompletions(hid, uid);
  const { completions: ownApproved } = useCompletions(hid, {
    status: "approved",
    completedBy: uid,
  });
  const { goals } = useGoals(hid, uid);
  const { goals: householdGoals } = useHouseholdGoals(hid);
  const familyGoalPick = pickFamilyGoalForDisplay(householdGoals);
  const familyGoalForCard = familyGoalPick?.goal ?? null;
  const familyGoalIsCelebrating = familyGoalPick?.isCelebrating ?? false;
  const { progress: familyProgress, breakdown: familyBreakdown } =
    useGoalProgress(hid, familyGoalForCard);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<typeof goals[number] | null>(null);
  const [submitFor, setSubmitFor] = useState<Chore | null>(null);
  const [activeGoalIdx, setActiveGoalIdx] = useState(0);
  const [treasureOpen, setTreasureOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);

  const treasure = useMemo(
    () => normalizeTreasurePrefs(userDoc?.prefs?.treasure),
    [userDoc?.prefs?.treasure],
  );
  const treasureProgress = useMemo(
    () => computeTreasureProgress(ownApproved, treasure.openedChestCount),
    [ownApproved, treasure.openedChestCount],
  );
  const meadowStage = treasureMeadowStage(treasure.ownedRewardIds.length);
  const heroScene = treasureRewardById(treasure.equipped?.scene);

  // Klik konkretnego celu → edycja. Klik "+ Dodaj cel" → tworzenie (null).
  const openGoalDialog = (goal: typeof goals[number] | null) => {
    setEditingGoal(goal);
    setGoalDialogOpen(true);
  };
  const closeGoalDialog = () => {
    setGoalDialogOpen(false);
    setEditingGoal(null);
  };

  const me = members.find((m) => m.userId === uid);
  const balance = me?.balanceMinor ?? 0;

  // Cel rodziny — listener i progres mountowane też w ChildDashboard, żeby
  // dziecko widziało wspólny widget na górze. Edycji nie ma (brak onEdit).

  // Cele posortowane: niezaliczone najbliżej celu first, zaliczone na końcu.
  // myGoal = aktualnie pokazywany w hero (swipe lub kropki przełącza idx).
  const sortedGoals = sortGoalsByPriority(goals, balance);
  const safeIdx = sortedGoals.length === 0
    ? 0
    : Math.min(activeGoalIdx, sortedGoals.length - 1);
  const myGoal = sortedGoals[safeIdx];

  // Reset index gdy lista się skraca (np. cel skasowany) lub zmienia się
  // priorytet — żeby nie pokazywać "out of bounds" lub starego celu.
  useEffect(() => {
    if (activeGoalIdx >= sortedGoals.length) setActiveGoalIdx(0);
  }, [sortedGoals.length, activeGoalIdx]);

  // Touch swipe między celami (mobile-first, dla 2+ celów).
  const touchStartXRef = useRef<number | null>(null);
  const onGoalTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };
  const onGoalTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartXRef.current;
    touchStartXRef.current = null;
    if (start === null || sortedGoals.length < 2) return;
    const delta = e.changedTouches[0].clientX - start;
    if (Math.abs(delta) < 40) return;
    setActiveGoalIdx((i) => {
      const next = delta > 0 ? i - 1 : i + 1;
      return Math.max(0, Math.min(sortedGoals.length - 1, next));
    });
  };
  const firstName = (me?.displayName ?? "").split(" ")[0] || "";
  const isFemale = genderFromRelation(me?.relation) === "f";

  // Auto-mark goal jako completed gdy saldo osiągnęło target.
  useAutoMarkGoalCompletion(hid, goals, balance);

  const myChores = chores.filter(
    (c) =>
      c.active &&
      !!uid &&
      isChoreActiveToday(c) &&
      isChoreForUser(c.assignedTo, uid),
  );

  // Resolve today's status per chore — preferuj approved > pending > rejected
  const statusByChore = new Map<string, "approved" | "pending" | "rejected">();
  for (const c of todays) {
    const cur = statusByChore.get(c.choreId);
    if (!cur || cur === "rejected") statusByChore.set(c.choreId, c.status);
    else if (cur === "pending" && c.status === "approved")
      statusByChore.set(c.choreId, c.status);
  }

  const total = myChores.length;
  const done = myChores.filter(
    (c) => statusByChore.get(c.id) === "approved",
  ).length;
  // Wszystkie zadania na dziś zgłoszone (pending lub approved) i jeszcze
  // nie wszystkie zatwierdzone → moment „dashboard-waiting" (Sianko czeka).
  // Rejected nie liczymy jako zgłoszone — dziecko musi powtórzyć.
  const allSubmitted =
    total > 0 &&
    myChores.every((c) => {
      const s = statusByChore.get(c.id);
      return s === "pending" || s === "approved";
    });

  // Pokaż wszystkie zadania na dziś. Sortowanie: todo/rejected → pending → done.
  // Brak slice — zrobione zostają widoczne jako feedback dla dziecka, na dole listy.
  const sortedTasks = [...myChores]
    .map((c) => {
      const s = statusByChore.get(c.id);
      const order = !s || s === "rejected" ? 0 : s === "pending" ? 1 : 2;
      return { c, s, order };
    })
    .sort((a, b) => a.order - b.order);

  // Wybór momentu (tekst + emocja) — priorytet: cel zdobyty > komplet
  // dnia > pusto > goal-progress > task-progress.
  const hasGoal = !!myGoal;
  const goalReached = hasGoal && balance >= myGoal.targetMinor;
  const remainingZl = hasGoal
    ? Math.max(0, Math.ceil((myGoal.targetMinor - balance) / 100))
    : undefined;
  const goalPct = hasGoal
    ? Math.min(100, (balance / myGoal.targetMinor) * 100)
    : 0;

  const moment: ChildMascotMoment = goalReached
    ? "dashboard-goal-reached"
    : total > 0 && done === total
      ? "dashboard-doing-end"
      : total === 0
        ? "dashboard-empty"
        : allSubmitted
          ? "dashboard-waiting"
          : hasGoal
            ? goalPct < 25
              ? "dashboard-goal-start"
              : goalPct < 75
                ? "dashboard-goal-mid"
                : "dashboard-goal-almost"
            : done === 0
              ? "dashboard-doing-start"
              : "dashboard-doing-mid";

  const { text: bubbleText } = childMascotSpeech(moment, {
    name: firstName,
    female: isFemale,
    balanceMinor: balance,
    goalTitle: myGoal?.title,
    goalEmoji: myGoal?.emoji,
    remainingZl,
    done,
    total,
  });
  const heroBubbleText = treasureCompanionMessage(
    treasure.equipped?.companion,
    done,
    total,
    bubbleText,
  );

  const handleQuickComplete = (c: Chore) => setSubmitFor(c);

  return (
    <div className={`kd-dashboard-stage-${meadowStage} -mx-4 -my-5 pb-6`}>
      {/* Bannery rytuałów — Friday Recap (pt-nd) i Sunday Plan (nd-pn).
          Grid jest 1-kolumnowy gdy widoczny tylko jeden, 2-kolumnowy gdy
          oba (CSS `:has()`). W niedzielę oba aktywne — jeden zamyka tydzień,
          drugi otwiera nowy. */}
      <div className="px-3.5 pt-3 mb-3 empty:hidden empty:mb-0 empty:pt-0">
        <div className="grid grid-cols-1 [&:has(>:nth-child(2))]:grid-cols-2 gap-2 empty:hidden">
          <RitualBanner compact />
          <SundayPlanBanner compact />
        </div>
      </div>
      {/* Hero hay block */}
      <div className="px-3.5 pt-3">
        <div
          className={`kd-meadow-hero kd-meadow-stage-${meadowStage} relative min-h-[214px] overflow-hidden rounded-[24px] border border-hay-bd px-5 pt-[18px]`}
          style={
            heroScene?.scenePath
              ? {
                  backgroundImage: `url("${heroScene.scenePath}")`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }
              : undefined
          }
        >
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,249,225,0.96)_0%,rgba(255,249,225,0.85)_46%,rgba(255,249,225,0.08)_78%)]" />
          {meadowStage >= 1 && (
            <div className="pointer-events-none absolute bottom-1 left-[47%] z-[1] text-[18px] drop-shadow-sm" aria-hidden>
              🌼 🌱
            </div>
          )}
          {meadowStage >= 2 && (
            <div className="pointer-events-none absolute right-[34%] top-[52px] z-[1] text-[14px]" aria-hidden>
              🦋
            </div>
          )}
          {meadowStage >= 3 && (
            <div className="pointer-events-none absolute right-[8%] top-[7px] z-[1] text-[15px]" aria-hidden>
              ✨
            </div>
          )}
          {/* Speech bubble (top-right) — tekst i emocja z childMascotSpeech.
              Tail po prawej = wskazuje na maskotkę (która jest po prawej). */}
          <div
            className="absolute z-[2] bg-white text-navy text-[12px] font-semibold leading-[1.3] rounded-[18px] px-3 py-2 border-[1.5px] border-line shadow-bubble"
            style={{ top: 16, right: 12, maxWidth: 158 }}
          >
            {heroBubbleText}
            <div
              className="absolute"
              style={{
                bottom: -6,
                right: 18,
                width: 12,
                height: 12,
                background: "#fff",
                transform: "rotate(45deg)",
                borderRight: "1.5px solid var(--kd-line)",
                borderBottom: "1.5px solid var(--kd-line)",
              }}
            />
          </div>

          {/* Balance + goal + streak — text content */}
          <div className="relative z-[1]">
            <div className="text-[14px] font-semibold" style={{ color: "#7A5A00" }}>
              Twoja skarbonka
            </div>
            <div className="mt-1.5">
              <MoneyText
                minor={balance}
                tone="money"
                variant="hero"
                size={54}
              />
            </div>

            {/* Goal: progress jeśli aktywny, inaczej mini-CTA "Wybierz cel".
                Gdy 2+ cele — swipe (touch) + kropki przełączają widoczny goal. */}
            {myGoal ? (
              <div
                className="mt-2.5 max-w-[68%]"
                onTouchStart={onGoalTouchStart}
                onTouchEnd={onGoalTouchEnd}
              >
                <GoalProgressInline
                  goal={myGoal}
                  progress={balance}
                  tone="purple"
                />
                {sortedGoals.length > 1 && (
                  <div
                    className="mt-1.5 flex items-center gap-1"
                    aria-label={`Cel ${safeIdx + 1} z ${sortedGoals.length}`}
                  >
                    {sortedGoals.map((g, i) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setActiveGoalIdx(i)}
                        aria-label={`Przełącz na cel ${i + 1}: ${g.title}`}
                        className={`h-1.5 rounded-full transition-all ${
                          i === safeIdx
                            ? "w-4 bg-forest"
                            : "w-1.5 bg-forest/30 hover:bg-forest/60"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => openGoalDialog(null)}
                className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold hover:opacity-80"
                style={{ color: "#7A5A00" }}
              >
                <span aria-hidden>🎯</span>
                Wybierz cel →
              </button>
            )}

          </div>

          {/* Mascot — bleeds out of the block on the right */}
          <HeroAvatar
            treasure={treasure}
            size={170}
            onClick={() => setCollectionOpen(true)}
            className="absolute -bottom-2 -right-2 z-[2]"
          />
        </div>
      </div>

      <div className="px-3.5 pt-2.5">
        <TreasureMeadowCard
          progress={treasureProgress}
          treasure={treasure}
          onOpenChest={() => setTreasureOpen(true)}
          onOpenCollection={() => setCollectionOpen(true)}
        />
      </div>

      {/* Tasks H2 row */}
      <div className="px-[18px] pt-[18px] pb-2 flex items-baseline justify-between gap-2">
        <h2 className="font-display text-[22px] font-extrabold text-navy m-0 tracking-tight flex items-baseline gap-2">
          Twoje zadania
          {total > 0 && (
            <span className="font-num text-[14px] font-bold text-slate">
              {done}/{total}
            </span>
          )}
        </h2>
        <Link
          to="/chores"
          className="text-[13px] font-bold text-green hover:text-forest"
        >
          Wszystkie →
        </Link>
      </div>

      {/* Task cards */}
      <div className="px-3.5 flex flex-col gap-2.5">
        {total === 0 ? (
          <Card>
            <CardBody className="py-8 text-center">
              <Mascot emotion="sleepy" size={88} className="mx-auto" />
              <p className="mt-3 text-sm text-slate font-semibold">
                Brak zadań na dziś.
              </p>
            </CardBody>
          </Card>
        ) : done === total ? (
          <Card>
            <CardBody className="py-10 text-center">
              <Mascot emotion="proud" size={104} className="mx-auto" />
              <p className="mt-4 font-display text-[18px] font-extrabold text-navy">
                Brawo, koniec zadań na dziś.
              </p>
              <p className="mt-1 text-sm text-slate">
                Wyjdź na dwór 🌳
              </p>
            </CardBody>
          </Card>
        ) : (
          sortedTasks.map(({ c, s }) => (
            <TaskCard
              key={c.id}
              chore={c}
              status={s}
              onComplete={() => handleQuickComplete(c)}
            />
          ))
        )}
      </div>

      {/* Goal block — cel osobisty (purple) + cel rodziny pod nim na stałe.
          Family card jest collapsible (slim header + tap rozwija breakdown).
          Empty-state CTA dla celu osobistego jest w hero pod skarbonką. */}
      <div className="px-3.5 pt-4 pb-[110px] space-y-2.5">
        {myGoal && (
          <button
            type="button"
            onClick={() => openGoalDialog(myGoal)}
            className="block w-full text-left rounded-[20px] overflow-hidden relative shadow-goal"
            style={{
              background:
                "linear-gradient(135deg, #8E7DF2 0%, #7565E8 100%)",
              padding: "14px 16px",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="text-[34px]" aria-hidden>
                {myGoal.emoji ?? "🎯"}
              </span>
              <div className="flex-1 text-white">
                <div className="font-display text-[18px] font-extrabold">
                  {myGoal.title}
                </div>
                <div className="font-num text-[12px] opacity-90 mt-0.5">
                  {Math.floor(balance / 100)} / {Math.floor(myGoal.targetMinor / 100)} zł ·{" "}
                  {Math.min(
                    100,
                    Math.round((balance / myGoal.targetMinor) * 100),
                  )}
                  %
                </div>
                <Progress
                  value={Math.min(
                    100,
                    (balance / myGoal.targetMinor) * 100,
                  )}
                  height={7}
                  tone="white"
                  trackClassName="bg-white/30"
                  className="mt-2"
                />
              </div>
            </div>
          </button>
        )}
        {familyGoalForCard && (
          <FamilyGoalCard
            goal={familyGoalForCard}
            progress={familyProgress}
            breakdown={familyBreakdown}
            members={members}
            isCelebrating={familyGoalIsCelebrating}
          />
        )}
      </div>

      {uid && (
        <GoalDialog
          open={goalDialogOpen}
          onClose={closeGoalDialog}
          ownerUid={uid}
          existingGoal={editingGoal}
          forcedKind="individual_money"
        />
      )}

      <CompletionSubmitSheet
        chore={submitFor}
        onClose={() => setSubmitFor(null)}
      />
      {uid && (
        <>
          <TreasureModal
            open={treasureOpen}
            onClose={() => setTreasureOpen(false)}
            uid={uid}
            progress={treasureProgress}
            treasure={treasure}
          />
          <TreasureCollectionSheet
            open={collectionOpen}
            onClose={() => setCollectionOpen(false)}
            uid={uid}
            treasure={treasure}
          />
        </>
      )}
    </div>
  );
}

// ─── TaskCard ──────────────────────────────────────────────────────────────
//
// Thin wrapper nad `ChoreTaskCard`. Pozostawiony jako osobny re-export dla
// stabilności call-sites (ChildDashboard / ParentDashboard). Nowy kod używa
// `ChoreTaskCard` bezpośrednio.

export function TaskCard({
  chore,
  status,
  onComplete,
  asXpOnly = false,
}: {
  chore: Chore;
  status: "approved" | "pending" | "rejected" | undefined;
  onComplete: () => void;
  /** Wymusza renderowanie nagrody jako XP. Patrz ChoreTaskCard.asXpOnly. */
  asXpOnly?: boolean;
}) {
  const isTodo = !status || status === "rejected";
  return (
    <ChoreTaskCard
      chore={chore}
      status={status}
      asXpOnly={asXpOnly}
      canDoIt={isTodo}
      onComplete={onComplete}
    />
  );
}

// ─── ParentDashboard — kept as-is (out of scope of v2 handoff) ───────────────
// This screen still uses the older layout; only primitives swapped colors.

export function ParentDashboard() {
  // useMemberSelfHeal jest teraz w AppShell — odpala się dla każdej
  // strony rodzica, nie tylko Dashboard.
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const { members } = useMembers(hid);
  const { completions: pendingCompletions } = useCompletions(hid, {
    status: "pending",
  });
  const { proposals: pendingProposals } = useChoreProposals(hid, {
    status: "pending",
  });
  const { submissions: pendingSubmissions } = useInstantSubmissions(hid, {
    status: "pending",
  });
  const { completions: approvedAll } = useCompletions(hid, {
    status: "approved",
  });

  const today = startOfDay(new Date()).getTime();
  const earnedToday = approvedAll
    .filter((c) => startOfDay(c.dueDate.toDate()).getTime() === today)
    .reduce((sum, c) => sum + c.rewardMinor, 0);

  const children = members.filter((m) => m.role === "child");
  const adults = members.filter((m) => m.role === "parent");
  const { transactions: weekEarns } = useHouseholdEarnsThisWeek(hid);

  const meParent = members.find((m) => m.userId === uid);
  const parentGender = genderFromRelation(meParent?.relation);
  const parentFirstName = (meParent?.displayName ?? "").split(" ")[0];
  // Tylko admin może dodawać domowników (Cloud Functions wymaga admina,
  // UI gating + Cloud Function check = belt + suspenders).
  const isAdmin = meParent?.isAdmin === true;
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [submitFor, setSubmitFor] = useState<Chore | null>(null);
  const [parentTasksOpen, setParentTasksOpen] = useState(false);

  // Cel rodziny — globalny widget. Edycji parent może (w odróżnieniu od dziecka).
  const { goals: householdGoals } = useHouseholdGoals(hid);
  const familyGoalPick = pickFamilyGoalForDisplay(householdGoals);
  const familyGoalForCard = familyGoalPick?.goal ?? null;
  const familyGoalIsCelebrating = familyGoalPick?.isCelebrating ?? false;
  const { progress: familyProgress, breakdown: familyBreakdown } =
    useGoalProgress(hid, familyGoalForCard);
  const [familyGoalDialogOpen, setFamilyGoalDialogOpen] = useState(false);
  const [editingFamilyGoal, setEditingFamilyGoal] = useState<
    typeof householdGoals[number] | null
  >(null);
  const openFamilyGoalDialog = (
    goal: typeof householdGoals[number] | null,
  ) => {
    setEditingFamilyGoal(goal);
    setFamilyGoalDialogOpen(true);
  };
  const closeFamilyGoalDialog = () => {
    setFamilyGoalDialogOpen(false);
    setEditingFamilyGoal(null);
  };

  // Twoje zadania (parent) — chore aktywne na dziś przypisane "any" lub
  // bezpośrednio rodzicowi. Rodzic dostaje za to tylko XP (snapshot w
  // createCompletion override kind=family, rewardMinor=0).
  const { chores: allChores } = useChores(hid);
  const { completions: myTodays } = useTodaysCompletions(hid, uid);
  const myChores = allChores.filter(
    (c) =>
      c.active &&
      !!uid &&
      isChoreActiveToday(c) &&
      isChoreForUser(c.assignedTo, uid),
  );
  const myStatusByChore = new Map<
    string,
    "approved" | "pending" | "rejected"
  >();
  for (const c of myTodays) {
    const cur = myStatusByChore.get(c.choreId);
    if (!cur || cur === "rejected") myStatusByChore.set(c.choreId, c.status);
    else if (cur === "pending" && c.status === "approved")
      myStatusByChore.set(c.choreId, c.status);
  }
  const mySortedTasks = [...myChores]
    .map((c) => {
      const s = myStatusByChore.get(c.id);
      const order = !s || s === "rejected" ? 0 : s === "pending" ? 1 : 2;
      return { c, s, order };
    })
    .sort((a, b) => a.order - b.order);
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 11) return "Dzień dobry";
    if (hour < 18) return "Cześć";
    return "Dobry wieczór";
  })();
  const pendingCount =
    pendingCompletions.length +
    pendingProposals.length +
    pendingSubmissions.length;
  const pendingPreview = [
    ...pendingCompletions.map((c) => ({
      id: `completion:${c.id}`,
      icon:
        c.kind === "family"
          ? "💛"
          : c.kind === "sport"
            ? "💪"
            : "📝",
      title: c.choreTitle,
      memberId: c.completedBy,
      label: "wykonane",
    })),
    ...pendingSubmissions.map((s) => ({
      id: `submission:${s.id}`,
      icon: s.icon ?? (s.kind === "family" ? "💛" : "⚡"),
      title: s.title,
      memberId: s.submittedBy,
      label: "wykonane od razu",
    })),
    ...pendingProposals.map((p) => ({
      id: `proposal:${p.id}`,
      icon: p.icon ?? "💡",
      title: p.title,
      memberId: p.proposedBy,
      label: "propozycja",
    })),
  ].slice(0, 3);
  const childCount = children.length;
  // Dni od ostatniego approved completion w domu (dowolne dziecko).
  // Steruje łagodnym tonem dymka gdy w domu cisza ≥5 dni.
  const daysSinceActivity = (() => {
    if (approvedAll.length === 0) return undefined;
    const newest = approvedAll.reduce((max, c) => {
      const t = c.completedAt.toDate().getTime();
      return t > max ? t : max;
    }, 0);
    return Math.floor((Date.now() - newest) / 86400000);
  })();
  // Blog promo — `getNextBlogPost` rotuje po liście, wybierając kolejny
  // nieobejrzany / widziany >7 dni temu. Opt-out przez prefs.blogPromos.
  const latestBlog = getNextBlogPost(userDoc?.prefs?.seenMoments);
  const blogOptIn = userDoc?.prefs?.blogPromos !== false;
  const showBlog = blogOptIn && !!latestBlog;
  const speech = parentMascotSpeech({
    name: parentFirstName,
    pendingCount,
    childCount,
    gender: parentGender,
    daysSinceActivity,
    blogPromo: showBlog && latestBlog
      ? { hook: latestBlog.hook, url: latestBlog.url }
      : undefined,
  });
  const speechText = speech.text;
  const speechUrl = speech.url;
  const isLinkedSpeech = !!speechUrl;
  const onSpeechClick = () => {
    if (!speechUrl || !latestBlog || !uid) return;
    // seen → przesuwa moment w rotacji (już wyświetlony); clicked → CTR
    // do panelu admin (analog seen, osobne pole prefs.clickedMoments).
    void markMomentSeen(uid, `blog:${latestBlog.slug}`).catch(() => {});
    void markMomentClicked(uid, `blog:${latestBlog.slug}`).catch(() => {});
    window.open(speechUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-4 pb-6">
      {/* Bannery rytuałów — 2 kolumny gdy oba aktywne, 1 kolumna gdy jeden.
          Friday Recap (pt-nd) zamyka tydzień, Sunday Plan (nd-pn) otwiera nowy. */}
      <div className="grid grid-cols-1 [&:has(>:nth-child(2))]:grid-cols-2 gap-2 empty:hidden">
        <RitualBanner compact />
        <SundayPlanBanner compact />
      </div>
      {/* Hero — maskotka rodzica + powitanie + dymek nad maskotką.
          W trybie empty-state (0 dzieci) maskotka wskazuje palcem w dół na
          kartę CTA poniżej; brak dymka, palec wystaje poza dolną krawędź. */}
      <div
        className="relative overflow-hidden rounded-[20px] bg-hay border border-hay-bd"
        style={{ minHeight: 132, padding: "14px 16px 8px" }}
      >
        <div className="relative z-[1] max-w-[58%]">
          <div className="text-[12px] font-semibold" style={{ color: "#7A5A00" }}>
            {greeting}
          </div>
          <div className="font-display text-[18px] font-extrabold text-navy mt-0.5 leading-tight">
            {parentFirstName || relationLabel(meParent)}
          </div>
          {/* Mini-chipy: do zatwierdzenia + zatwierdzone dziś. */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Link
              to="/approvals"
              className="inline-flex items-center gap-1.5 rounded-full bg-white border-[1.5px] border-line px-2.5 py-1 hover:border-green transition-colors"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-pending" />
              <span className="font-num text-[13px] font-extrabold text-navy">
                {pendingCount}
              </span>
              <span className="text-[10px] text-slate font-semibold">
                do zatw.
              </span>
            </Link>
            <Link
              to="/wallet"
              className="inline-flex items-center gap-1.5 rounded-full bg-white border-[1.5px] border-line px-2.5 py-1 hover:border-green transition-colors"
            >
              <span className="text-[13px]" aria-hidden>💰</span>
              <span className="font-num text-[13px] font-extrabold text-forest">
                {(earnedToday / 100).toFixed(2).replace(".", ",")} zł
              </span>
              <span className="text-[10px] text-slate font-semibold">dziś</span>
            </Link>
          </div>
        </div>
        {/* Dymek — speech text przy maskotce. Treść z parentMascotSpeech
            (zmienna w zależności od childCount/pendingCount/pory dnia).
            Klikalna gdy speech to obiekt z URL (blog promo) — otwiera
            artykuł w nowej karcie i zapisuje seenMoment. */}
        {isLinkedSpeech ? (
          <button
            type="button"
            onClick={onSpeechClick}
            className="absolute z-[2] bg-white text-navy text-[12px] font-semibold leading-snug rounded-[16px] px-3 py-2 border-[1.5px] border-accent shadow-bubble text-left hover:bg-accent-soft transition-colors cursor-pointer"
            style={{ top: 12, right: 79, maxWidth: 140 }}
          >
            <span className="block">{speechText}</span>
            <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-accent uppercase tracking-wide">
              Przeczytaj na blogu →
            </span>
            <span
              aria-hidden
              className="absolute"
              style={{
                bottom: -6,
                right: 4,
                width: 12,
                height: 12,
                background: "#fff",
                transform: "rotate(45deg)",
                borderRight: "1.5px solid var(--kd-accent)",
                borderBottom: "1.5px solid var(--kd-accent)",
              }}
            />
          </button>
        ) : (
          <div
            className="absolute z-[2] bg-white text-navy text-[12px] font-semibold leading-snug rounded-[16px] px-3 py-2 border-[1.5px] border-line shadow-bubble"
            style={{ top: 12, right: 79, maxWidth: 140 }}
          >
            {speechText}
            <span
              aria-hidden
              className="absolute"
              style={{
                bottom: -6,
                right: 4,
                width: 12,
                height: 12,
                background: "#fff",
                transform: "rotate(45deg)",
                borderRight: "1.5px solid var(--kd-line)",
                borderBottom: "1.5px solid var(--kd-line)",
              }}
            />
          </div>
        )}
        {/* Maskotka — finger-down gdy 0 dzieci (palec lekko wystaje poza
            dolną krawędź hero, wskazując na empty-state CTA poniżej),
            inaczej idle pose dosunięta nisko (od pasa w górę). */}
        <div
          className="absolute z-[1] pointer-events-none"
          style={(() => {
            // Mama jest na obrazku odrobinę niżej wycentrowana niż tata,
            // więc kompensujemy 10px w górę w jej wariancie.
            const momLift = parentGender === "f" ? 10 : 0;
            return childCount === 0
              ? { right: -4, bottom: -110 + momLift }
              : { right: -14, bottom: -90 + momLift };
          })()}
          aria-hidden
        >
          <ParentMascot
            gender={parentGender}
            pose={speech.pose}
            variant={speech.variant}
            size={childCount === 0 ? 240 : 210}
            shadow={false}
          />
        </div>
      </div>


      {/* Checklist pierwszego tygodnia — golden path dla nowej rodziny.
          Znika sam po komplecie albo po X (localStorage). */}
      {hid && (
        <FirstWeekChecklist
          hid={hid}
          onAddChild={() => setAddChildOpen(true)}
        />
      )}

      {/* Cel rodziny — globalny widget na górze. Aktywny cel = card z postępem
          i breakdown'em. Brak celu + parent = CTA do utworzenia (limit 1 active). */}
      {familyGoalForCard ? (
        <FamilyGoalCard
          goal={familyGoalForCard}
          progress={familyProgress}
          breakdown={familyBreakdown}
          members={members}
          isCelebrating={familyGoalIsCelebrating}
          onEdit={
            familyGoalIsCelebrating
              ? undefined
              : () => openFamilyGoalDialog(familyGoalForCard)
          }
        />
      ) : (
        children.length > 0 && (
          <button
            type="button"
            onClick={() => openFamilyGoalDialog(null)}
            className="w-full rounded-[18px] border border-dashed border-line bg-card px-4 py-4 text-left transition-[transform] active:translate-y-px"
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted">
              ⭐ Cel rodziny
            </div>
            <div className="text-[14px] font-semibold text-navy mt-1">
              Razem zbieracie zadania pomocy 💛 albo aktywność poza ekranem 🌳
            </div>
            <div className="text-[11px] text-muted mt-1">
              Wspólna nagroda na koniec — wybieracie razem
            </div>
          </button>
        )
      )}

      {/* Empty-state CTA gdy 0 dzieci: otwiera AddChildSheet inline. Bez
          gating na isAdmin — Cloud Function i tak waliduje admina,
          a brak flagi często wynika z opóźnionego backfill. */}
      {childCount === 0 && (
        <Card
          role="button"
          tabIndex={0}
          onClick={() => setAddChildOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setAddChildOpen(true);
            }
          }}
          className="cursor-pointer transition hover:border-green focus:outline-none focus-visible:ring-2 focus-visible:ring-green"
        >
          <CardBody className="py-6 text-center">
            <div className="text-3xl mb-2">👶</div>
            <p className="font-display text-[16px] font-extrabold text-navy">
              Załóż konto dziecku
            </p>
            <p className="text-xs text-slate mt-1">
              Bez dziecka nie ma jak zacząć — dodaj pierwszego domownika
            </p>
            <Button
              variant="primary"
              size="sm"
              className="mt-3"
              onClick={(e) => {
                e.stopPropagation();
                setAddChildOpen(true);
              }}
            >
              Dodaj dziecko
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Twoje zadania (parent self-service). Rodzic gra dla XP
          (TaskCard z asXpOnly). Layout zależy od ilości:
          - 1-2 zadania: lista bezpośrednio jak u dziecka (bez Card wrappera).
          - 3+ zadań: accordion w Card z chevronem i licznikiem,
            żeby nie zdominować dashboardu rodzica długą listą. */}
      {mySortedTasks.length > 0 && mySortedTasks.length <= 2 && (
        <div>
          <h2 className="px-1 mb-2 font-display text-[18px] font-extrabold text-navy inline-flex items-baseline gap-2">
            Twoje zadania
            <span className="font-num text-[12px] text-slate font-bold">
              +XP
            </span>
          </h2>
          <div className="flex flex-col gap-2">
            {mySortedTasks.map(({ c, s }) => (
              <TaskCard
                key={c.id}
                chore={c}
                status={s}
                onComplete={() => setSubmitFor(c)}
                asXpOnly
              />
            ))}
          </div>
        </div>
      )}
      {mySortedTasks.length > 2 && (
        <Card>
          <button
            type="button"
            onClick={() => setParentTasksOpen((v) => !v)}
            aria-expanded={parentTasksOpen}
            className={cn(
              "w-full px-4 py-3 flex items-center justify-between gap-2 text-left transition-colors hover:bg-cloud rounded-t-[18px]",
              parentTasksOpen && "border-b border-line",
            )}
          >
            <div className="inline-flex items-center gap-2 text-[15px] font-display font-extrabold text-navy">
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  parentTasksOpen && "rotate-180",
                )}
              />
              Twoje zadania
              <span className="font-num text-[12px] text-slate font-bold">
                +XP
              </span>
            </div>
            <span className="font-num text-[12px] text-slate font-semibold">
              {mySortedTasks.length} zad.
            </span>
          </button>
          {parentTasksOpen && (
            <div className="px-4 pb-4 pt-2 flex flex-col gap-2">
              {mySortedTasks.map(({ c, s }) => (
                <TaskCard
                  key={c.id}
                  chore={c}
                  status={s}
                  onComplete={() => setSubmitFor(c)}
                  asXpOnly
                />
              ))}
            </div>
          )}
        </Card>
      )}

      <Link to="/approvals" className="block">
        <Card className="transition hover:border-green">
          <CardHeader className="flex items-center justify-between">
            <CardTitle>
              <ShieldCheck className="inline h-3.5 w-3.5 mr-1 -mt-0.5" /> Czeka
              na ciebie
            </CardTitle>
            <span className="text-xs text-green font-bold">Wszystkie</span>
          </CardHeader>
          {pendingCount === 0 ? (
            <CardBody className="py-8 text-center text-sm text-slate">
              Brak zgłoszeń ✨
            </CardBody>
          ) : (
            <ul className="divide-y divide-line">
              {pendingPreview.map((s) => {
                const child = members.find((m) => m.userId === s.memberId);
                return (
                  <li
                    key={s.id}
                    className="px-4 py-3 flex items-center gap-3"
                  >
                    <span className="text-xl w-7 text-center">{s.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {s.title}
                      </div>
                      <div className="text-[11px] text-slate">
                        {child?.displayName ?? "—"} · {s.label}
                      </div>
                    </div>
                    <StatusBadge status="pending" />
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </Link>

      {children.length > 0 && (
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Dzieci</CardTitle>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setAddChildOpen(true)}
                className="text-xs text-green font-bold hover:text-forest"
              >
                + Dodaj
              </button>
            )}
          </CardHeader>
          <ul className="divide-y divide-line">
            {children.map((m) => (
              <li key={m.userId}>
                <Link
                  to={`/stats?member=${m.userId}`}
                  className="px-4 py-3 flex items-center gap-3 transition hover:bg-cloud"
                >
                  <div className="h-10 w-10 rounded-[12px] bg-hay flex items-center justify-center text-xl font-bold">
                    {m.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{m.displayName}</div>
                    <div className="text-[11px] text-slate">Saldo</div>
                  </div>
                  <MoneyText minor={m.balanceMinor} tone="money" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <WeeklyFunderBreakdown transactions={weekEarns} adults={adults} />

      <Link to="/chores/week" className="block">
        <Button variant="primary" block>
          <CalendarDays className="h-4 w-4" /> Plan tygodnia
        </Button>
      </Link>

      <AddChildSheet
        open={addChildOpen}
        onClose={() => setAddChildOpen(false)}
      />

      <CompletionSubmitSheet
        chore={submitFor}
        onClose={() => setSubmitFor(null)}
      />

      <GoalDialog
        open={familyGoalDialogOpen}
        onClose={closeFamilyGoalDialog}
        existingGoal={editingFamilyGoal}
      />
    </div>
  );
}
